import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, Phone, Loader2, Inbox, RefreshCw, Send, Menu, X, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";

const STATUSES = ["new", "contacted", "closed"];
const STATUS_STYLE = {
  new: "bg-[#d4c4b7]/15 text-[#d4c4b7] border-[#d4c4b7]/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-400/30",
  closed: "bg-stone-500/15 text-stone-400 border-stone-500/30",
};

const NAV_LINKS = [
  { href: "/admin/content", label: "Content", testid: "admin-content-link" },
  { href: "/admin/projects", label: "Projects", testid: "admin-projects-link" },
  { href: "/admin/reviews", label: "Reviews", testid: "admin-reviews-link" },
  { href: "/admin/guides", label: "Guide", testid: "admin-guides-link" },
  { href: "/admin/opportunities", label: "SEO Tracker", testid: "admin-opportunities-link" },
  { href: "/admin/ads", label: "Google Ads", testid: "admin-ads-link" },
  { href: "/admin/seo", label: "Site SEO", testid: "admin-seo-link" },
];

function StatCard({ label, value, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left border p-6 transition-colors ${active ? "border-[#d4c4b7] bg-[#141416]" : "border-white/10 bg-[#0f0f11] hover:border-white/25"}`}
    >
      <p className="overline text-stone-500 mb-3">{label}</p>
      <p className="font-serif text-4xl text-stone-50">{value}</p>
    </button>
  );
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const sendRecap = async () => {
    setSendingRecap(true);
    try {
      const { data } = await api.post("/admin/weekly-summary/send");
      const to = (data.delivered_to && data.delivered_to.length) ? data.delivered_to.join(", ") : null;
      if (data.status === "sent") toast.success(to ? `Weekly recap emailed to ${to}` : "Weekly recap sent.");
      else toast.error("Recap email could not be sent — check the notification inbox.");
    } catch {
      toast.error("Could not send recap.");
    } finally {
      setSendingRecap(false);
    }
  };

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  const load = useCallback(async () => {
    try {
      const [e, s] = await Promise.all([api.get("/enquiries"), api.get("/enquiries/stats")]);
      setEnquiries(e.data);
      setStats(s.data);
    } catch {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const changeStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((x) => (x.id === id ? data : x)));
      const s = await api.get("/enquiries/stats");
      setStats(s.data);
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries((list) => list.filter((x) => x.id !== id));
      const s = await api.get("/enquiries/stats");
      setStats(s.data);
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  const visible = filter === "all" ? enquiries : enquiries.filter((e) => e.status === filter);

  if (!ready || !user) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c]" data-testid="admin-dashboard">
      <div className="grain-overlay" aria-hidden />

      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/logo.png" alt="Perth Tiling WA" className="h-9 md:h-10 w-auto object-contain" />
            <span className="hidden sm:block overline text-stone-500">Enquiries</span>
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-3">
            <span className="hidden xl:block text-sm text-stone-400">{user.email}</span>
            <a href="/admin/leads" data-testid="admin-leads-link" className="inline-flex items-center gap-2 bg-[#9c7a4d] hover:bg-[#b08d5a] text-white px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors">Leads</a>
            {NAV_LINKS.map((n) => (
              <a key={n.href} href={n.href} data-testid={n.testid} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors">{n.label}</a>
            ))}
            <button data-testid="admin-weekly-recap" onClick={sendRecap} disabled={sendingRecap} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors">
              {sendingRecap ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              <span className="hidden xl:inline">Weekly Recap</span>
            </button>
            <button data-testid="admin-refresh" onClick={load} className="p-2 text-stone-400 hover:text-white transition-colors" aria-label="Refresh"><RefreshCw className="h-4 w-4" /></button>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors"><LogOut className="h-3.5 w-3.5" /> Logout</button>
          </div>

          {/* Mobile: primary Leads CTA + hamburger */}
          <div className="flex lg:hidden items-center gap-2">
            <a href="/admin/leads" data-testid="admin-leads-link-mobile" className="inline-flex items-center bg-[#9c7a4d] hover:bg-[#b08d5a] text-white px-3.5 py-2 text-[0.65rem] uppercase tracking-[0.18em] transition-colors">Leads</a>
            <button data-testid="admin-refresh" onClick={load} className="p-2 text-stone-400 hover:text-white transition-colors" aria-label="Refresh"><RefreshCw className="h-5 w-5" /></button>
            <button data-testid="admin-menu-toggle" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu" className="p-2 text-stone-200 hover:text-white transition-colors border border-white/15">
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {menuOpen && (
          <div className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl px-5 py-4 space-y-2" data-testid="admin-mobile-menu">
            <p className="text-xs text-stone-500 pb-1 truncate">{user.email}</p>
            <div className="grid grid-cols-2 gap-2">
              {NAV_LINKS.map((n) => (
                <a key={n.href} href={n.href} data-testid={`${n.testid}-m`} className="block text-center px-3 py-3 text-[0.65rem] uppercase tracking-[0.18em] text-stone-200 hover:text-[#d4c4b7] border border-white/12 hover:border-[#d4c4b7] transition-colors">{n.label}</a>
              ))}
            </div>
            <button data-testid="admin-weekly-recap-m" onClick={() => { setMenuOpen(false); sendRecap(); }} disabled={sendingRecap} className="w-full inline-flex items-center justify-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] disabled:opacity-60 px-4 py-3 text-[0.65rem] uppercase tracking-[0.18em] transition-colors">
              {sendingRecap ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Weekly Recap
            </button>
            <button data-testid="admin-logout-m" onClick={() => { logout(); navigate("/admin/login"); }} className="w-full inline-flex items-center justify-center gap-2 border border-white/15 hover:border-red-500/50 text-stone-300 hover:text-red-300 px-4 py-3 text-[0.65rem] uppercase tracking-[0.18em] transition-colors"><LogOut className="h-3.5 w-3.5" /> Logout</button>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-10">
        <h1 className="font-serif font-light text-stone-50 text-4xl md:text-5xl mb-10">Enquiries Dashboard</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <StatCard testid="stat-all" label="All Enquiries" value={stats.total} active={filter === "all"} onClick={() => setFilter("all")} />
          <StatCard testid="stat-new" label="New" value={stats.new} active={filter === "new"} onClick={() => setFilter("new")} />
          <StatCard testid="stat-contacted" label="Contacted" value={stats.contacted} active={filter === "contacted"} onClick={() => setFilter("contacted")} />
          <StatCard testid="stat-closed" label="Closed" value={stats.closed} active={filter === "closed"} onClick={() => setFilter("closed")} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" />
          </div>
        ) : visible.length === 0 ? (
          <div data-testid="admin-empty" className="border border-white/10 bg-[#0f0f11] py-24 text-center">
            <Inbox className="h-10 w-10 text-stone-600 mx-auto mb-4" />
            <p className="text-stone-400">No {filter !== "all" ? filter : ""} enquiries yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="enquiry-list">
            {visible.map((e) => (
              <div key={e.id} data-testid={`enquiry-row-${e.id}`} className="border border-white/10 bg-[#0f0f11] p-6 md:p-8 hover:border-white/20 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <h3 className="font-serif text-2xl text-stone-50">{e.name}</h3>
                      <span className={`text-[0.6rem] uppercase tracking-[0.18em] px-3 py-1 border ${STATUS_STYLE[e.status]}`}>{e.status}</span>
                      <span className="overline text-stone-600">{fmt(e.created_at)}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
                      <a href={`tel:${e.phone}`} className="inline-flex items-center gap-2 text-sm text-stone-300 hover:text-[#d4c4b7] transition-colors">
                        <Phone className="h-3.5 w-3.5 text-[#d4c4b7]" /> {e.phone}
                      </a>
                      <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 text-sm text-stone-300 hover:text-[#d4c4b7] transition-colors">
                        <Mail className="h-3.5 w-3.5 text-[#d4c4b7]" /> {e.email}
                      </a>
                      <span className="text-sm text-[#d4c4b7]">{e.service}</span>
                      {e.suburb && <span className="text-xs text-stone-500">Suburb: <span className="text-stone-300">{e.suburb}</span></span>}
                      {e.budget && <span className="text-xs text-stone-500">Budget: <span className="text-stone-300">{e.budget}</span></span>}
                    </div>
                    <p className="text-stone-400 font-light leading-relaxed whitespace-pre-wrap max-w-3xl">{e.message}</p>
                    {e.photos && e.photos.length > 0 && (
                      <div className="mt-5" data-testid={`enquiry-photos-${e.id}`}>
                        <div className="flex items-center gap-4 mb-3">
                          <p className="text-[0.6rem] uppercase tracking-[0.18em] text-stone-500">{e.photos.length} photo{e.photos.length > 1 ? "s" : ""} attached</p>
                          <a
                            href={`${API}/enquiries/${e.id}/photos.zip?auth=${localStorage.getItem("ptwa_token")}`}
                            download
                            data-testid={`enquiry-photos-download-${e.id}`}
                            className="inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.18em] text-[#d4c4b7] hover:text-white border border-white/15 hover:border-[#d4c4b7] px-3 py-1.5 transition-colors"
                          >
                            <Download className="h-3 w-3" /> Download All
                          </a>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          {e.photos.map((p) => {
                            const url = `${API}/enquiries/photo/${p.storage_path}?auth=${localStorage.getItem("ptwa_token")}`;
                            return (
                              <a
                                key={p.id}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`enquiry-photo-${e.id}`}
                                className="relative h-24 w-24 border border-white/15 overflow-hidden group/photo hover:border-[#d4c4b7] transition-colors"
                                title={p.filename}
                              >
                                <img src={url} alt={p.filename} className="h-full w-full object-cover group-hover/photo:scale-105 transition-transform duration-500" loading="lazy" />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-row lg:flex-col gap-3 shrink-0">
                    <select
                      data-testid={`status-select-${e.id}`}
                      value={e.status}
                      onChange={(ev) => changeStatus(e.id, ev.target.value)}
                      className="bg-[#141416] border border-white/15 text-stone-200 text-sm px-4 py-2 outline-none focus:border-[#d4c4b7] cursor-pointer"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-[#141416]">{s}</option>
                      ))}
                    </select>
                    <button
                      data-testid={`delete-${e.id}`}
                      onClick={() => remove(e.id)}
                      className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-red-500/60 text-stone-400 hover:text-red-400 px-4 py-2 text-sm transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
